import { useState, useEffect, useRef } from 'react'
import { Search, Loader2 } from 'lucide-react'
import { api } from '@/lib/api'
import { debounce } from '@/lib/utils'
import type { SearchResult } from '@/types'

interface Props {
  onSelect: (result: SearchResult) => void
  placeholder?: string
}

export default function StockSearch({ onSelect, placeholder = 'Search stocks...' }: Props) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const search = debounce(async (q: string) => {
    if (!q.trim() || q.length < 1) {
      setResults([])
      setIsOpen(false)
      return
    }

    setIsLoading(true)
    try {
      const res = await api.searchStocks(q)
      if (res.success && res.data) {
        setResults(res.data)
        setIsOpen(true)
      }
    } finally {
      setIsLoading(false)
    }
  }, 300)

  useEffect(() => {
    search(query)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelect = (result: SearchResult) => {
    onSelect(result)
    setQuery('')
    setResults([])
    setIsOpen(false)
  }

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
        {isLoading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30 animate-spin" />
        )}
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results.length > 0 && setIsOpen(true)}
          placeholder={placeholder}
          className="input-base w-full pl-9 pr-9"
        />
      </div>

      {isOpen && results.length > 0 && (
        <div className="absolute top-full mt-1 w-full bg-[#1e1f23] border border-white/10 rounded-xl shadow-2xl z-50 overflow-hidden animate-fade-in">
          {results.map((result) => (
            <button
              key={result.symbol}
              onClick={() => handleSelect(result)}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/[0.06] transition-colors text-left"
            >
              <div className="w-8 h-8 bg-white/[0.06] rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-bold text-white/70">
                  {result.symbol.slice(0, 2)}
                </span>
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-white">{result.symbol}</div>
                <div className="text-xs text-white/40 truncate">{result.description}</div>
              </div>
              <div className="ml-auto text-[10px] text-white/20 uppercase tracking-wider">
                {result.type}
              </div>
            </button>
          ))}
        </div>
      )}

      {isOpen && !isLoading && results.length === 0 && query.length > 1 && (
        <div className="absolute top-full mt-1 w-full bg-[#1e1f23] border border-white/10 rounded-xl shadow-2xl z-50 px-4 py-6 text-center animate-fade-in">
          <div className="text-sm text-white/30">No results for "{query}"</div>
        </div>
      )}
    </div>
  )
}
